import { useCallback, useEffect, useState } from "react";
import {
  ArrowsClockwise,
  Eye,
  EyeSlash,
  PencilSimple,
  Pause,
  Play,
  Plus,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getLearningAdminSnapshot,
  setLearningObservationFamilyVisibility,
  upsertLearningFocus,
} from "@/lib/learningObservations";

const inputClass = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-neutral-50";

function localDate(value = new Date()) {
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return localDate(date);
}

function emptyFocusDraft() {
  return { id: null, label: "", description: "", sortOrder: 100, isActive: true };
}

function formatDateTime(value) {
  if (!value) return "Time not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time not recorded";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Desktop-first administrator workspace for local focus configuration and
 * explicit family publication. It intentionally avoids scores, frameworks,
 * or automatic messaging. */
export default function AdminLearning() {
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => localDate());
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [focusDraft, setFocusDraft] = useState(emptyFocusDraft);

  const load = useCallback(async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      setSnapshot(await getLearningAdminSnapshot({ from, to }));
    } catch (loadError) {
      setError(loadError?.message || "HoneyAcorn could not load learning observations.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const observations = snapshot?.observations || [];
  const focuses = snapshot?.focuses || [];
  const sharedCount = observations.filter((observation) => observation.familyVisible).length;
  const awaitingReviewCount = observations.length - sharedCount;

  const mutate = async (successMessage, action) => {
    setSaving(true);
    try {
      await action();
      toast.success(successMessage);
      await load({ background: true });
      return true;
    } catch (mutationError) {
      toast.error(mutationError?.message || "The change could not be saved.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const setDraftValue = (key, value) => setFocusDraft((current) => ({ ...current, [key]: value }));

  const startEditingFocus = (focus) => {
    setFocusDraft({
      id: focus.id,
      label: focus.label,
      description: focus.description || "",
      sortOrder: focus.sortOrder,
      isActive: focus.isActive,
    });
  };

  const submitFocus = async (event) => {
    event.preventDefault();
    const isEdit = Boolean(focusDraft.id);
    const saved = await mutate(
      isEdit ? "Focus area saved" : "Focus area added",
      () => upsertLearningFocus({
        focusId: focusDraft.id,
        label: focusDraft.label,
        description: focusDraft.description,
        isActive: focusDraft.isActive,
        sortOrder: Number(focusDraft.sortOrder),
      }),
    );
    if (saved) setFocusDraft(emptyFocusDraft());
  };

  const toggleFocus = async (focus) => {
    await mutate(
      focus.isActive ? "Focus area paused" : "Focus area activated",
      () => upsertLearningFocus({
        focusId: focus.id,
        label: focus.label,
        description: focus.description,
        isActive: !focus.isActive,
        sortOrder: focus.sortOrder,
      }),
    );
  };

  const changeFamilyVisibility = async (observation, visible) => {
    if (visible && !window.confirm("Share this observation with the family? This does not send a notification.")) return;
    await mutate(
      visible ? "Observation shared with family" : "Observation hidden from family",
      () => setLearningObservationFamilyVisibility({ observationId: observation.id, visible }),
    );
  };

  if (loading && !snapshot) {
    return <div data-testid="admin-learning-loading" className="flex justify-center py-24" aria-label="Loading learning observations"><div className="h-9 w-9 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div>;
  }

  if (error && !snapshot) {
    return (
      <EmptyPanel
        icon={WarningCircle}
        title="Learning observations are unavailable"
        action={<button type="button" onClick={() => load()} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">Try again</button>}
      >
        {error}
      </EmptyPanel>
    );
  }

  return (
    <div data-testid="admin-learning" className="space-y-6">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Operations</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">Learning observations</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-neutral-600">
            Review teacher-noticed moments, choose what families can see, and configure optional focus labels for this center. HoneyAcorn does not add a developmental framework, score, or automated family message.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-semibold text-neutral-600">From<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="mt-1 block rounded-xl border border-neutral-200 bg-white px-2.5 py-2 text-sm text-neutral-900" /></label>
          <label className="text-xs font-semibold text-neutral-600">To<input type="date" value={to} onChange={(event) => setTo(event.target.value)} className="mt-1 block rounded-xl border border-neutral-200 bg-white px-2.5 py-2 text-sm text-neutral-900" /></label>
          <button type="button" aria-label="Refresh learning observations" onClick={() => load({ background: true })} disabled={refreshing || saving} className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-primary-hover transition hover:bg-primary/5 disabled:opacity-60"><ArrowsClockwise size={18} weight="bold" className={refreshing ? "animate-spin" : ""} /></button>
        </div>
      </header>

      {error ? <section data-testid="admin-learning-error" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</section> : null}

      <section className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Observations" value={observations.length} />
        <SummaryCard label="Awaiting family review" value={awaitingReviewCount} emphasis={awaitingReviewCount > 0} />
        <SummaryCard label="Shared with families" value={sharedCount} />
      </section>

      <section className="grid gap-6 2xl:grid-cols-[minmax(0,1.35fr)_minmax(330px,0.65fr)]">
        <div className="space-y-5">
          <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex gap-3">
              <Sparkle size={21} weight="duotone" className="mt-0.5 shrink-0 text-primary-hover" />
              <div>
                <h2 className="font-heading text-base font-semibold text-neutral-900">Family visibility stays human</h2>
                <p className="mt-1 text-sm leading-6 text-neutral-700">Teachers can capture a moment quickly. An administrator must explicitly share it before a family can see it, and sharing does not send a notification.</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-white shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
              <div>
                <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">Observation review</h2>
                <p className="mt-1 text-sm text-neutral-600">{snapshot?.windowStart && snapshot?.windowEnd ? `${snapshot.windowStart} to ${snapshot.windowEnd}` : "Recent center observations"}</p>
              </div>
              <span className="rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">{observations.length} in view</span>
            </div>

            {observations.length === 0 ? (
              <div data-testid="admin-learning-observations-empty" className="p-10 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><Sparkle size={20} weight="duotone" className="text-primary-hover" /></div>
                <h3 className="mt-3 font-heading text-base font-semibold text-neutral-900">No observations in this window</h3>
                <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-neutral-500">When teachers save a learning moment, it will appear here for review. No family content is created automatically.</p>
              </div>
            ) : (
              <ul data-testid="admin-learning-observation-list" className="divide-y divide-neutral-100">
                {observations.map((observation) => (
                  <li key={observation.id} className="p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-heading text-base font-semibold text-neutral-900">{observation.childName}</h3>
                          {observation.focusLabel ? <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary-hover">{observation.focusLabel}</span> : null}
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${observation.familyVisible ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>{observation.familyVisible ? "Shared" : "Needs review"}</span>
                        </div>
                        <p className="mt-1 text-xs text-neutral-500">{observation.classroomName} · {formatDateTime(observation.observedAt)}{observation.createdByName ? ` · Added by ${observation.createdByName}` : ""}</p>
                        <p className="mt-3 max-w-3xl whitespace-pre-wrap text-sm leading-6 text-neutral-700">{observation.note}</p>
                      </div>
                      <button
                        type="button"
                        data-testid={`admin-learning-visibility-${observation.id}`}
                        disabled={saving}
                        onClick={() => changeFamilyVisibility(observation, !observation.familyVisible)}
                        className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition disabled:opacity-60 ${observation.familyVisible ? "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50" : "border-primary bg-primary text-white hover:bg-primary-hover"}`}
                      >
                        {observation.familyVisible ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                        {observation.familyVisible ? "Hide from family" : "Share with family"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-card">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">Center configuration</p>
              <h2 className="mt-1 font-heading text-lg font-semibold tracking-tight text-neutral-900">Optional focus areas</h2>
              <p className="mt-1 text-sm leading-6 text-neutral-600">Use your center’s own language. A focus is a label for teachers, not a score, benchmark, or required program.</p>
            </div>

            <form onSubmit={submitFocus} className="mt-5 space-y-3 border-t border-neutral-100 pt-5">
              <label className="block"><span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Focus name</span><input required maxLength={120} value={focusDraft.label} onChange={(event) => setDraftValue("label", event.target.value)} placeholder="Creative expression" className={inputClass} /></label>
              <label className="block"><span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Teacher guidance <span className="normal-case tracking-normal">(optional)</span></span><textarea maxLength={1000} rows={3} value={focusDraft.description} onChange={(event) => setDraftValue("description", event.target.value)} placeholder="How your center uses this optional label…" className={`${inputClass} resize-y`} /></label>
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3"><label className="block"><span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Order</span><input type="number" min="0" max="100000" value={focusDraft.sortOrder} onChange={(event) => setDraftValue("sortOrder", event.target.value)} className={inputClass} /></label><label className="mb-2 inline-flex items-center gap-2 text-sm font-medium text-neutral-700"><input type="checkbox" checked={focusDraft.isActive} onChange={(event) => setDraftValue("isActive", event.target.checked)} className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary/30" />Active</label></div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button type="submit" disabled={saving || !focusDraft.label.trim()} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"><Plus size={16} weight="bold" />{focusDraft.id ? "Save focus" : "Add focus"}</button>
                {focusDraft.id ? <button type="button" disabled={saving} onClick={() => setFocusDraft(emptyFocusDraft())} className="rounded-full px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-60">Cancel</button> : null}
              </div>
            </form>
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-card">
            <div className="flex items-center justify-between gap-3"><h2 className="font-heading text-base font-semibold text-neutral-900">Configured focuses</h2><span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-600">{focuses.length}</span></div>
            {focuses.length === 0 ? <p data-testid="admin-learning-focuses-empty" className="mt-4 text-sm leading-6 text-neutral-500">No focus area is required. Add one only when your center has a useful label for teachers.</p> : <ul className="mt-4 space-y-2">{focuses.map((focus) => <li key={focus.id} className="rounded-xl border border-neutral-100 p-3"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-medium text-neutral-900">{focus.label}</p><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${focus.isActive ? "bg-emerald-50 text-emerald-800" : "bg-neutral-100 text-neutral-600"}`}>{focus.isActive ? "Active" : "Paused"}</span></div>{focus.description ? <p className="mt-1 text-sm leading-5 text-neutral-500">{focus.description}</p> : null}</div><div className="flex shrink-0 gap-1"><button type="button" aria-label={`Edit ${focus.label}`} onClick={() => startEditingFocus(focus)} className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"><PencilSimple size={16} weight="bold" /></button><button type="button" aria-label={`${focus.isActive ? "Pause" : "Activate"} ${focus.label}`} disabled={saving} onClick={() => toggleFocus(focus)} className="rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-60">{focus.isActive ? <Pause size={16} weight="bold" /> : <Play size={16} weight="bold" />}</button></div></div></li>)}</ul>}
          </section>
        </aside>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, emphasis = false }) {
  return <section className={`rounded-2xl border p-4 ${emphasis ? "border-amber-200 bg-amber-50" : "border-neutral-200 bg-white"}`}><p className={`text-xs font-bold uppercase tracking-[0.15em] ${emphasis ? "text-amber-900" : "text-neutral-500"}`}>{label}</p><p className={`mt-2 font-heading text-3xl font-semibold tracking-tight ${emphasis ? "text-amber-950" : "text-neutral-900"}`}>{value}</p></section>;
}

function EmptyPanel({ icon: Icon, title, children, action }) {
  return <section className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center"><Icon size={26} weight="duotone" className="mx-auto text-neutral-400" /><h1 className="mt-3 font-heading text-lg font-semibold text-neutral-900">{title}</h1><p className="mx-auto mt-1 max-w-lg text-sm leading-6 text-neutral-600">{children}</p><div className="mt-4">{action}</div></section>;
}
